// Thermal Print Service (TPS 2.x) API client.
// The service runs on the till at http://127.0.0.1:38445 and only answers
// https://billing.dhaba.ai. A browser must be paired once: POST /pair asks the
// person at the till to allow it and returns a token that every other call
// sends as `Authorization: Bearer <token>`.

import type {
  TPSEnvelope,
  TPSJob,
  TPSJobStatusResponse,
  TPSPairResult,
  TPSPing,
  TPSPrintersResponse,
  TPSPrintRequest,
  TPSPrintResponse,
} from "@/types/printer";
import { DEMO_PRINTING_KEY_PREFIX, TPS_TOKEN_KEY_PREFIX, isFinalJobStatus } from "@/types/printer";
import { appConfig } from "@/lib/config";
import { getSelectedContext } from "@/lib/api";

/** A normal request gives up after this long (Chrome holds a request while its local-network prompt is open). */
export const TPS_REQUEST_TIMEOUT_MS = 10_000;
/** POST /pair waits up to 60 s for the person at the till; allow some slack on top. */
export const TPS_PAIR_TIMEOUT_MS = 75_000;
/** Job id returned when a demo outlet skips printing. */
export const DEMO_SKIP_JOB_ID = "demo-skip";

export type TPSConnectionFailure = "unreachable" | "timeout";

/** The browser could not reach the service at all (not running, blocked, or no answer in time). */
export class TPSConnectionError extends Error {
  constructor(public reason: TPSConnectionFailure = "unreachable") {
    super(
      reason === "timeout"
        ? "The Dhaba Print Service did not answer in time"
        : "The Dhaba Print Service on this computer is not reachable",
    );
    this.name = "TPSConnectionError";
  }
}

function errorCode(data: unknown): string | null {
  return data && typeof data === "object" && "code" in data && typeof data.code === "string" ? data.code : null;
}

/** A user-facing sentence for an error answer from the service. */
export function tpsErrorMessage(
  code: string | null,
  data: unknown,
  status: number,
  retryAfterSeconds: number | null = null,
): string {
  switch (code) {
    case "PAIRING_REQUIRED":
      return "This browser is not connected to the Dhaba Print Service on this computer yet.";
    case "PAIRING_DENIED":
      return "The request was denied on this computer.";
    case "PAIRING_TIMEOUT":
      return "Nobody answered the prompt on this computer in time.";
    case "PAIRING_IN_PROGRESS":
      return "Another connection request is still open — wait a moment and try again.";
    case "RATE_LIMITED":
      return retryAfterSeconds
        ? `Too many requests. Try again in ${retryAfterSeconds} seconds.`
        : "Too many requests. Try again in a minute.";
    case "PRINTER_NOT_FOUND":
      return "That printer is not set up in the print service on this computer. Choose the printer again in Settings › Print.";
    case "ESCPOS_FORBIDDEN_COMMAND":
      return "The print service refused this print because it contains a printer command that is not allowed. Please contact Dhaba.ai support.";
    case "PAYLOAD_TOO_LARGE":
      return "This print is too large for the print service.";
    case "JOB_NOT_FOUND":
      return "The print service no longer has this print job.";
    case "JOB_NOT_CANCELLABLE":
      return "This print has already started and can't be cancelled.";
    case "SHUTTING_DOWN":
      return "The print service is shutting down. Try again when it has restarted.";
    case "INTERNAL":
      return "The print service ran into a problem. Try again; if it keeps happening, restart Dhaba Print Service.";
    case "INVALID_REQUEST":
    case "UNSUPPORTED_MEDIA_TYPE": {
      const detail =
        data && typeof data === "object" && "error" in data && typeof data.error === "string" ? data.error : "";
      return `The print service couldn't read this print${detail ? ` (${detail})` : ""}. If it keeps happening, contact Dhaba.ai support.`;
    }
  }
  return `The print service couldn't do this (${code ?? `error ${status}`}). Try again; if it keeps happening, restart Dhaba Print Service.`;
}

/** The service answered with an error. `code` is the service's machine code (see TPSErrorCode). */
export class TPSError extends Error {
  public code: string | null;

  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
    public retryAfterSeconds: number | null = null,
  ) {
    super(tpsErrorMessage(errorCode(data), data, status, retryAfterSeconds));
    this.name = "TPSError";
    this.code = errorCode(data);
  }
}

/** 401 PAIRING_REQUIRED — this browser has no valid token (never paired, or the pairing was revoked). */
export class TPSPairingRequiredError extends TPSError {
  constructor(data: unknown = { success: false, error: "pairing required", code: "PAIRING_REQUIRED" }) {
    super(401, "Unauthorized", data);
    this.name = "TPSPairingRequiredError";
    this.code = "PAIRING_REQUIRED";
  }
}

// ==========================================
// Pairing token (per browser profile, per TPS address)
// ==========================================

const TOKEN_EVENT = "dhaba:tps-token";

function tokenKey(): string {
  return `${TPS_TOKEN_KEY_PREFIX}${appConfig.tpsBaseUrl}`;
}

export function getTpsToken(): string | null {
  try {
    return localStorage.getItem(tokenKey());
  } catch {
    return null;
  }
}

function announceToken(): void {
  try {
    window.dispatchEvent(new Event(TOKEN_EVENT));
  } catch {
    /* no window (tests without DOM) */
  }
}

export function setTpsToken(token: string): void {
  try {
    localStorage.setItem(tokenKey(), token);
    // Remote printing: a browser that was ever paired prints here first
    // (src/lib/remotePrint.ts neverPaired).
    localStorage.setItem("dhaba_tps_ever_paired", "1");
  } catch {
    /* storage unavailable: the pairing lasts for this page only */
  }
  announceToken();
}

export function clearTpsToken(): void {
  try {
    localStorage.removeItem(tokenKey());
  } catch {
    /* ignore */
  }
  announceToken();
}

/** Calls `onChange` when the token changes in this tab or another tab. */
export function subscribeTpsToken(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === tokenKey()) onChange();
  };
  window.addEventListener(TOKEN_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(TOKEN_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
  };
}

// ==========================================
// Demo outlet printing opt-in (per outlet, saved on this device)
// ==========================================

function demoPrintingKey(): string {
  const outletId = getSelectedContext()?.outlet?.id ?? "default";
  return `${DEMO_PRINTING_KEY_PREFIX}${outletId}`;
}

export function isDemoPrintingEnabled(): boolean {
  try {
    return localStorage.getItem(demoPrintingKey()) === "true";
  } catch {
    return false;
  }
}

/**
 * A demo outlet whose printing is off on this device: nothing prints here and
 * nothing is handed to a till (QA M20: "No till is online" / "didn't print"
 * toasts stacked up with demo printing off).
 */
export function demoPrintingOff(): boolean {
  return getSelectedContext()?.outlet?.is_demo === true && !isDemoPrintingEnabled();
}

export function setDemoPrintingEnabled(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(demoPrintingKey(), "true");
    else localStorage.removeItem(demoPrintingKey());
  } catch {
    /* ignore storage errors */
  }
}

// ==========================================
// Transport
// ==========================================

interface TpsFetchOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  /** Send the pairing token (every route except GET /ping and POST /pair). */
  auth?: boolean;
  timeoutMs?: number;
  signal?: AbortSignal;
}

function retryAfter(response: Response): number | null {
  const value = Number.parseInt(response.headers.get("Retry-After") ?? "", 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

async function tpsFetch<T>(endpoint: string, options: TpsFetchOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, timeoutMs = TPS_REQUEST_TIMEOUT_MS, signal } = options;

  const headers: Record<string, string> = {};
  const token = auth ? getTpsToken() : null;
  if (auth) {
    // No token: the service would answer 401 anyway, so don't send the request.
    if (!token) throw new TPSPairingRequiredError();
    headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const onCallerAbort = () => controller.abort();
  signal?.addEventListener("abort", onCallerAbort);

  let response: Response;
  try {
    response = await fetch(`${appConfig.tpsBaseUrl}${endpoint}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    if (signal?.aborted && !timedOut) throw error;
    throw new TPSConnectionError(timedOut ? "timeout" : "unreachable");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onCallerAbort);
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401) {
      // Revoked or unknown token: forget it (unless it was replaced meanwhile).
      if (token && getTpsToken() === token) clearTpsToken();
      throw new TPSPairingRequiredError(data ?? undefined);
    }
    throw new TPSError(response.status, response.statusText, data, retryAfter(response));
  }

  return data as T;
}

export interface WaitForJobOptions {
  /** Stop watching after this long (the job keeps going in the service). */
  timeoutMs?: number;
  /** Check this job at a fixed pace instead of the default (every second at first, then every 3 s). */
  intervalMs?: number;
  signal?: AbortSignal;
  onUpdate?: (job: TPSJob) => void;
}

export interface WaitForJobResult {
  /** Last state seen, or null if the service never answered. */
  job: TPSJob | null;
  /** The job reached a final state (completed, failed, failed_unknown, expired, cancelled). */
  final: boolean;
}

// ==========================================
// Job watcher: one shared poll for every job this page follows
// ==========================================
//
// The service allows 300 requests a minute per site. Instead of one GET /jobs/{id}
// per job per second, every followed job is read from one GET /jobs/recent, every
// second for the first few seconds (most prints finish there) and every 3 s after.
// A job that has dropped out of the recent list (a busy till) is asked for by id.

/** Check every second while a job is young… */
export const JOB_POLL_FAST_MS = 1_000;
/** …for this long… */
export const JOB_POLL_FAST_FOR_MS = 5_000;
/** …then every 3 s. */
export const JOB_POLL_MS = 3_000;
/** How many of the newest jobs GET /jobs/recent returns (the service's default). */
export const RECENT_JOBS_LIMIT = 50;

interface JobWatch {
  jobId: string;
  started: number;
  deadline: number;
  intervalMs: number | null;
  nextDue: number;
  job: TPSJob | null;
  onUpdate?: (job: TPSJob) => void;
  done: (result: WaitForJobResult | Error) => void;
}

const watches = new Set<JobWatch>();
let watchTimer: ReturnType<typeof setTimeout> | null = null;
let polling = false;
/** Set by a 429: no job check before this time. */
let pausedUntil = 0;

function watchInterval(watch: JobWatch, now: number): number {
  if (watch.intervalMs != null) return watch.intervalMs;
  return now - watch.started < JOB_POLL_FAST_FOR_MS ? JOB_POLL_FAST_MS : JOB_POLL_MS;
}

function finishWatch(watch: JobWatch, result: WaitForJobResult | Error): void {
  if (!watches.delete(watch)) return;
  watch.done(result);
}

function scheduleWatches(): void {
  if (polling) return; // rescheduled when the running check ends
  if (watchTimer !== null) {
    clearTimeout(watchTimer);
    watchTimer = null;
  }
  if (watches.size === 0) return;
  let next = Infinity;
  for (const watch of watches) next = Math.min(next, watch.nextDue);
  next = Math.max(next, pausedUntil);
  watchTimer = setTimeout(() => {
    watchTimer = null;
    void checkWatches();
  }, Math.max(0, next - Date.now()));
}

interface JobsAnswer {
  jobs: Map<string, TPSJob>;
  /** The service no longer has these jobs. */
  gone: Map<string, TPSError>;
}

async function fetchWatchedJobs(ids: string[]): Promise<JobsAnswer> {
  const recent = await tpsFetch<TPSEnvelope<{ jobs: TPSJob[] }>>(`/jobs/recent?limit=${RECENT_JOBS_LIMIT}`);
  const jobs = new Map<string, TPSJob>();
  for (const job of recent.data?.jobs ?? []) jobs.set(job.job_id, job);
  const gone = new Map<string, TPSError>();
  for (const id of ids) {
    if (jobs.has(id)) continue;
    try {
      jobs.set(id, (await tps.getJobStatus(id)).data);
    } catch (error) {
      if (error instanceof TPSError && error.code === "JOB_NOT_FOUND") gone.set(id, error);
      else throw error;
    }
  }
  return { jobs, gone };
}

async function checkWatches(): Promise<void> {
  const now = Date.now();
  const due = [...watches].filter((watch) => watch.nextDue <= now);
  if (due.length === 0) {
    scheduleWatches();
    return;
  }
  polling = true;
  try {
    let answer: JobsAnswer | null = null;
    try {
      answer = await fetchWatchedJobs([...new Set(due.map((watch) => watch.jobId))]);
    } catch (error) {
      if (error instanceof TPSPairingRequiredError) {
        // The token is gone: no job can be followed any more.
        for (const watch of [...watches]) finishWatch(watch, error);
        return;
      }
      if (error instanceof TPSError && error.code === "RATE_LIMITED") {
        pausedUntil = Date.now() + (error.retryAfterSeconds ?? 1) * 1000;
      }
      // Anything else (service restarting, a passing 500): ride it out until the deadline.
    }

    if (answer) {
      for (const watch of [...watches]) {
        const gone = answer.gone.get(watch.jobId);
        if (gone) {
          finishWatch(watch, gone);
          continue;
        }
        const job = answer.jobs.get(watch.jobId);
        if (!job) continue;
        watch.job = job;
        watch.onUpdate?.(job);
        if (isFinalJobStatus(job.status)) finishWatch(watch, { job, final: true });
      }
    }

    const after = Date.now();
    for (const watch of due) {
      if (!watches.has(watch)) continue;
      const next = Math.max(after + watchInterval(watch, after), pausedUntil);
      if (next > watch.deadline) finishWatch(watch, { job: watch.job, final: false });
      else watch.nextDue = next;
    }
  } finally {
    polling = false;
    scheduleWatches();
  }
}

function watchJob(jobId: string, options: WaitForJobOptions): Promise<WaitForJobResult> {
  const { timeoutMs = 60_000, intervalMs, signal, onUpdate } = options;
  return new Promise<WaitForJobResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const now = Date.now();
    const watch: JobWatch = {
      jobId,
      started: now,
      deadline: now + timeoutMs,
      intervalMs: intervalMs ?? null,
      nextDue: now,
      job: null,
      onUpdate,
      done: (result) => {
        signal?.removeEventListener("abort", onAbort);
        if (result instanceof Error) reject(result);
        else resolve(result);
      },
    };
    const onAbort = () => {
      finishWatch(watch, new DOMException("Aborted", "AbortError"));
      scheduleWatches();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    watches.add(watch);
    scheduleWatches();
  });
}

// ==========================================
// API
// ==========================================

export const tps = {
  /** Is the service running? Needs no pairing. */
  ping: (signal?: AbortSignal) => tpsFetch<TPSEnvelope<TPSPing>>("/ping", { auth: false, signal }),

  /**
   * Ask the person at the till to allow this site. Resolves with the token (also saved)
   * once they click Allow; rejects with PAIRING_DENIED / PAIRING_TIMEOUT (403),
   * PAIRING_IN_PROGRESS (409) or RATE_LIMITED (429).
   */
  pair: async (signal?: AbortSignal): Promise<TPSPairResult> => {
    const res = await tpsFetch<TPSEnvelope<TPSPairResult>>("/pair", {
      method: "POST",
      auth: false,
      timeoutMs: TPS_PAIR_TIMEOUT_MS,
      signal,
    });
    setTpsToken(res.data.token);
    return res.data;
  },

  /** Revoke this browser's token on the service (best effort) and forget it here. */
  unpair: async (): Promise<void> => {
    try {
      if (getTpsToken()) await tpsFetch("/pair", { method: "DELETE" });
    } catch {
      /* the service may be down; forgetting the token locally is what matters */
    } finally {
      clearTpsToken();
    }
  },

  /** List all available printers with live status */
  getPrinters: () => tpsFetch<TPSPrintersResponse>("/printers"),

  /**
   * Queue a print job (202). Demo outlets only reach hardware when printing is enabled for them;
   * otherwise this answers `{job_id: "demo-skip", status: "completed"}` without calling the service.
   */
  print: async (request: TPSPrintRequest): Promise<TPSPrintResponse> => {
    if (getSelectedContext()?.outlet?.is_demo === true && !isDemoPrintingEnabled()) {
      return {
        success: true,
        data: { job_id: DEMO_SKIP_JOB_ID, status: "completed" },
      };
    }
    return tpsFetch<TPSPrintResponse>("/print", { method: "POST", body: request });
  },

  /** Check print job status */
  getJobStatus: (jobId: string) => tpsFetch<TPSJobStatusResponse>(`/jobs/${encodeURIComponent(jobId)}`),

  /** Cancel a job that is still queued or waiting to retry (409 JOB_NOT_CANCELLABLE otherwise). */
  cancelJob: (jobId: string) =>
    tpsFetch<TPSEnvelope<{ cancelled: boolean }>>(`/jobs/${encodeURIComponent(jobId)}`, { method: "DELETE" }),

  /**
   * Follow a job until it is final or `timeoutMs` passes. All followed jobs share one
   * GET /jobs/recent poll. Short outages and 429s are ridden out; a lost pairing or a
   * job the service no longer has ends the wait with an error.
   */
  waitForJob: (jobId: string, options: WaitForJobOptions = {}): Promise<WaitForJobResult> => watchJob(jobId, options),
};
